import { emit, listen } from "@tauri-apps/api/event";
import { isTauri } from "@/lib/is-tauri";
import { useCanvasStore } from "@/stores/canvas-store";
import { withMutationEmitSuppressed } from "@/lib/mirrored-store";
import { safeTauriUnlisten } from "./tauri-listener-cleanup";

const CURSOR_INPUT_DISMISSED_EVENT = "denker://cursor-input-dismissed";

// Suppressed: this runs in EVERY window via the dismissal event below, so
// each window's clear is a local view reset. Without suppression every
// mirror would echo the clear upstream and the owner would re-broadcast
// it N more times per dismissal.
function clearCursorInputState() {
  withMutationEmitSuppressed(() => {
    const store = useCanvasStore.getState();
    store.setPendingCursorSummon(null);
    store.closeCursorInput();
  });
}

/**
 * Close only this window's cursor input without crossing the mirror
 * boundary. For per-window view resets (mount, space change, interaction
 * scope change) that must never clear a bar open on another display.
 */
export function closeCursorInputLocal() {
  withMutationEmitSuppressed(() => {
    useCanvasStore.getState().closeCursorInput();
  });
}

/**
 * Drop only this window's pending summon without crossing the mirror
 * boundary. For per-window takeovers (leaving compact mode, dictation
 * claiming the bar surface) that must not clear a summon anchored to a
 * different display.
 */
export function clearPendingCursorSummonLocal() {
  withMutationEmitSuppressed(() => {
    useCanvasStore.getState().setPendingCursorSummon(null);
  });
}

export function dismissCursorInputEverywhere() {
  clearCursorInputState();
  if (!isTauri()) return;
  void emit(CURSOR_INPUT_DISMISSED_EVENT).catch(() => {});
}

export function listenForCursorInputDismissal() {
  if (!isTauri()) return () => {};

  let unlisten: (() => void) | null = null;
  let cancelled = false;
  void listen(CURSOR_INPUT_DISMISSED_EVENT, () => {
    clearCursorInputState();
  }).then((u) => {
    if (cancelled) safeTauriUnlisten(u);
    else unlisten = u;
  });

  return () => {
    cancelled = true;
    if (unlisten) safeTauriUnlisten(unlisten);
  };
}
